import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Calendar, Close, Delete } from "@dreamhub/designsystem/icons";
import ShiftDatesBar from "./ShiftDatesBar";
import { DateShift } from "./types";

interface DuplicateBulkActionsBarProps {
  selectedCount: number;
  totalCount: number;
  onShift: (shift: DateShift) => void;
  onPickStartDate: (date: Date) => void;
  onDelete: () => void;
  onClearSelection: () => void;
}

const DuplicateBulkActionsBar: React.FC<DuplicateBulkActionsBarProps> = ({
  selectedCount,
  totalCount,
  onShift,
  onPickStartDate,
  onDelete,
  onClearSelection
}) => {
  const { t } = useTranslation();
  const [isShiftMode, setIsShiftMode] = useState(false);

  useEffect(() => {
    if (selectedCount === 0) setIsShiftMode(false);
  }, [selectedCount]);

  return (
    <div
      className="fixed bottom-28 left-1/2 transform -translate-x-1/2 z-50"
      data-test="duplicate-bulk-actions-bar"
    >
      <div className="flex flex-row items-center justify-between w-[600px] h-14 bg-surface-elevation3 border border-border-border1 rounded-2xl shadow-lg px-4 py-3">
        {isShiftMode ? (
          <ShiftDatesBar
            appliesToAll={selectedCount === totalCount}
            onApplyShift={onShift}
            onApplyStartDate={onPickStartDate}
            onClose={() => setIsShiftMode(false)}
          />
        ) : (
          <>
            <Text variant="body" className="font-medium">
              {t("contracts.duplicate.selectedCount", {
                count: selectedCount
              })}
            </Text>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsShiftMode(true)}
                data-test="duplicate-bulk-shift-dates"
              >
                <IconContainer size="sm" className="mr-1">
                  <Calendar />
                </IconContainer>
                {t("contracts.duplicate.shiftDates.title")}
              </Button>
              <Button
                variant="secondary"
                onClick={onDelete}
                data-test="duplicate-bulk-delete"
              >
                <IconContainer size="sm" className="mr-1">
                  <Delete />
                </IconContainer>
                {t("delete")}
              </Button>
              <div className="w-[1px] h-8 bg-border-border2 mx-2" />
              <IconButton
                variant="iconNoBg"
                size="md"
                iconColor="secondary"
                onClick={onClearSelection}
                data-test="duplicate-bulk-clear"
              >
                <Close />
              </IconButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DuplicateBulkActionsBar;
